import { markChatPanelClosed, scheduleMobileHideRetries } from '../chat-session';
import { loadScriptOnce } from './load-script';
import type { ChatAdapter, ChatLoadContext } from './types';

function ze(...args: unknown[]): void {
  window.zE?.(...args);
}

/** Run when the Zendesk snippet is ready (queues if still booting). */
function whenZeReady(fn: () => void): void {
  if (typeof window.zE !== 'function') {
    fn();
    return;
  }
  try {
    window.zE(fn);
  } catch {
    fn();
  }
}

function applyIdentify(username: string | null): void {
  if (!username || !window.zE) return;
  ze('webWidget', 'prefill', {
    name: { value: username, readOnly: true },
  });
}

function hideLauncher(): void {
  whenZeReady(() => {
    ze('webWidget', 'hide');
    try {
      ze('messenger', 'hide');
    } catch {
      /* ignore */
    }
  });
}

function onReady(ctx: ChatLoadContext): void {
  whenZeReady(() => {
    applyIdentify(ctx.username);
    ze('webWidget', 'setLocale', ctx.language);

    if (ctx.isMobile) {
      hideLauncher();
      ze('webWidget:on', 'close', () => {
        markChatPanelClosed();
      });
      ze('webWidget:on', 'open', () => {
        ctx.onUnread(0);
      });
    }

    ze('webWidget:on', 'chat:unreadMessages', (msgNumber: unknown) => {
      const n = typeof msgNumber === 'number' ? msgNumber : Number(msgNumber) || 0;
      ctx.onUnread(n);
    });
  });
}

export const zendeskAdapter: ChatAdapter = {
  id: 'zendesk',

  async load(ctx) {
    await loadScriptOnce({
      id: 'ze-snippet',
      src: `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(ctx.key)}`,
    });
    onReady(ctx);
    if (ctx.isMobile) {
      scheduleMobileHideRetries(hideLauncher);
    }
  },

  open() {
    whenZeReady(() => {
      // show before open — after a prior hide the launcher/panel must be visible again.
      ze('webWidget', 'show');
      ze('webWidget', 'open');
      try {
        ze('messenger', 'show');
        ze('messenger', 'open');
      } catch {
        /* ignore */
      }
    });
  },

  hideLauncher,

  identify(username) {
    applyIdentify(username);
  },
};
